import type { BrandStyle, MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
// TYPE-ONLY on purpose: the signature needs the kit's shape, but `brandKit.ts` pulls in the live prompt
// serializers at runtime. A type import is erased, so this module stays a leaf with no product-tree edges.
import type { BrandKit } from '~/design-system/components/DeckGen/birtvy/brandKit'
// The stored shape, for its `logoUrl` column. This file stays a leaf: a type-only edge, no runtime
// import, and `withStoredMark` reads the column defensively so a plain BrandKit still passes.
import { kitMarkUrl, type StoredBrandKit } from '~/design-system/components/DeckGen/birtvy/brandKitWire'
import { backgroundIsLight } from '~/design-system/components/DeckGen/utils/brandLogoContrast'
// The OKLab core, REUSED rather than re-derived. That module is a leaf too — its only import is a `type`
// — so taking its maths costs this file nothing in edges, and there is exactly one set of Ottosson
// matrices in the codebase. `srgbToLinear` is module-private there, so the WCAG luminance below carries
// its own copy of the transfer function; everything else comes from here.
import {
  isNeutralHex,
  oklabOfHex,
  rgbToHex,
  type Oklab,
} from '~/design-system/components/DeckGen/birtvy/web/birtvyBrandTruth'
// The single type-scale derivation, shared with the Typography panel and the kit agent. `typeScale.ts`
// imports nothing, so taking it costs this leaf no edges; what it buys is that the five sizes the panel
// shows and the five sizes the recipe states are computed by one function rather than agreeing by luck.
import { clampScaleToBands, typeScaleForKit } from './typeScale'
import {
  emptyStyle,
  mergeStyles,
  normalizeHex,
  type LayoutPattern,
  type Style,
  type StyleConfidence,
  type StyleDensity,
  type StyleDepth,
  type StyleField,
  type StyleFontFace,
  type StyleSource,
  type StyleVoice,
  type StyleVoiceExample,
} from './styleRecord'

/**
 * IMPORTERS — every style source Deck has, written into one {@link Style}.
 *
 * `styleRecord.ts` states the rule this file exists to satisfy: **every source is an importer, never a
 * format.** A website scrape, a vision synthesis, a saved kit and an artifact's own `:root` block all end
 * up as the same record, so a renderer never learns where a value came from — it reads the provenance.
 *
 * CONFIDENCE IS WHY THIS FILE IS INTERESTING. The audit's finding was that a hex read out of a stylesheet
 * and a hex a vision model guessed are both `string`, so the guess silently overwrites the fact (an image
 * wired to a slide beats a wired website on the per-slide path today, which is exactly backwards). Every
 * write here goes through `put`, which cannot land a value without stamping how much to trust it, and
 * `mergeStyles` then settles precedence by that stamp instead of by call order.
 *
 *   website      → `measured`. Hexes and font families are CSS declarations. The palette and roles are
 *                  re-ranked client-side against the page's own screenshot histogram
 *                  (`birtvy/web/birtvyBrandTruth.ts`) — still measured, because a pixel is a measurement
 *                  and that pass only ever replaces a declared colour with another declared colour.
 *                  A DERIVED field keeps `measured` only while it is still a value the sheet states: the
 *                  spacing step stays measured when the ramp declares it and drops to `inferred` when it
 *                  is a divisor that appears nowhere; density is a RATIO, so it is always `inferred`; a
 *                  font family repaired out of a synthetic fallback name is `inferred` too.
 *   moodboard /  → `inferred`, all of it. A vision model looked at pictures and decided.
 *   image
 *   brand kit    → both, per field: the embedded `brandStyle` half is measured (exact CSS), the
 *                  `MoodboardProfile` half is inferred. The merge does the picking.
 *   artifact     → `manual` + `measured`: a person or a model TYPED those tokens, and reading a `:root`
 *                  declaration is reading a real artifact.
 *
 * WHAT THIS FILE REFUSES TO GUESS is as load-bearing as what it extracts. An absent field is what lets a
 * preset fill it (`styleGaps`); a plausible wrong number outranks that preset forever, because
 * `mergeStyles` ranks `inferred` above `default`. So the prose parsers below take a value only when the
 * sentence states one outright, and several fields are deliberately never derived at all — each refusal
 * is commented where it happens.
 *
 * ROLES ARE VALIDATED WHERE THEY ARE ASSIGNED. A colour role is not a colour, it is a JOB — and a role
 * nobody can read is not doing it. Every importer ends by auditing `text`, `muted` and `accent` against
 * `bg` and repairing whatever cannot be read, because that is the one place the fix reaches all six
 * sources and every consumer at once. See the legibility section for the granola.ai measurement that
 * forced it.
 *
 * PURE: no React, no fetch, no clock, no randomness. Ids come from the source's own identity, so the same
 * input always produces the same record (a playground can diff two runs and see only real differences).
 *
 * @see styleRecord.ts  the contract
 * @see STYLE_SEMANTICS.md
 */

// --- tuning constants ------------------------------------------------------------------------------

/** Spacing values outside this band are not rhythm steps: below 2px is a hairline or a border, above
 *  200px is a container width or a hero height that happened to be declared in the same list. */
const SPACE_MIN_PX = 2
const SPACE_MAX_PX = 200

/** The smallest number we will call a base step. Real systems bottom out at 4 (Tailwind's 0.25rem); a
 *  derived step of 1–3 is not a grid, it is what you get when the values share no common factor. */
const MIN_STEP_PX = 4

/**
 * Below this a declared length is a HAIRLINE, not a rhythm step — a border, a focus ring, an optical nudge.
 *
 * MEASURED (2026-08-13, 5 brands x 20 generations): `space` came back `{}` for four of five sources and
 * `{unit: 4}` for granola.ai, because the gcd of a ramp that contains a 4 is at most 4 however large the
 * rest of it is. A 4 is not harmless: `styleRender.spaceLines` turns the unit into "gaps of 3–5 steps
 * between blocks", so a unit of 4 asks for 12–20px gutters and cramps every slide. A ramp like
 * [4,8,12,16,24,32,48] has a step of 8 and a hairline of 4; dropping the hairline before the arithmetic is
 * what separates the two.
 */
const HAIRLINE_MAX_PX = 8

/** How much of the ramp a DECLARED value has to divide before we call it the step rather than one rung.
 *  Two thirds, because a real system carries a stray value or two — a 12 in an otherwise 8-based ramp — and
 *  a rule that demands every value refuses every real site. */
const STEP_MAJORITY = 2 / 3

/** Fewest values a ramp needs before its spread describes anything. Two values give a "typical" gap that is
 *  just their midpoint: an arithmetic artefact, not a rhythm. */
const MIN_RAMP_FOR_DENSITY = 3

/** Where the ramp's typical gap sits relative to its base step, in multiples of that step. A system whose
 *  everyday gap is 3+ steps is spending its ramp on air; one that never leaves 1.5 steps is spending it on
 *  proximity. Between the two is the unremarkable middle, which is a real answer and not a shrug. */
const AIRY_RATIO = 3
const TIGHT_RATIO = 1.5

/** Root font size assumed when a spacing scale is written in rem. THE ONE ASSUMPTION IN THIS FILE. A site
 *  with a different root shifts every value uniformly, and the gcd below shifts with it, so the derived
 *  step stays internally consistent even where the absolute px is wrong. */
const REM_PX = 16

/** `type.scale` is a RATIO BETWEEN STEPS; anything outside this is a display-to-body ratio wearing the
 *  wrong name (see the refusal in `proseTypeScale`). */
const SCALE_MIN = 1.05
const SCALE_MAX = 3

// --- sources ---------------------------------------------------------------------------------------

/**
 * A WEBSITE's CSS-extracted brand → Style. The only importer that measures rather than guesses.
 *
 * `bs.typography` is a record of CSS facts (heading weight/transform/letter-spacing/line-height, radius),
 * so those become tokens directly. `bs.spacing` is a string[] the backend works to extract and which
 * NOTHING in production reads today — it becomes `space.unit` here, which is the first time that
 * extraction has ever paid for itself.
 */
export function styleFromBrandStyle(bs: BrandStyle, opts?: { name?: string; origin?: string }): Style {
  const origin = opts?.origin ?? bs.url ?? 'website CSS'
  const name = opts?.name ?? hostLabel(bs.url) ?? 'Website style'
  const style = emptyStyle(`website:${slug(origin)}`, name)
  const put = writer(style, 'website', origin)

  const palette = unique(compact((bs.palette ?? []).map((c) => normalizeHex(c))))
  put('color.palette', palette.length > 0 ? palette : undefined, 'measured', (v) => {
    style.color.palette = v
  })

  // `roles` is a loose Record on the wire; these six keys are its documented vocabulary (shared with
  // MoodboardProfile.roles). An unknown key is dropped rather than guessed at — the raw palette above
  // still carries the colour, which is what `palette` is for.
  const roles = bs.roles ?? {}
  put('color.bg', normalizeHex(roles.background), 'measured', (v) => {
    style.color.bg = v
  })
  put('color.surface', normalizeHex(roles.surface), 'measured', (v) => {
    style.color.surface = v
  })
  put('color.text', normalizeHex(roles.text), 'measured', (v) => {
    style.color.text = v
  })
  put('color.muted', normalizeHex(roles.textMuted), 'measured', (v) => {
    style.color.muted = v
  })
  put('color.accent', normalizeHex(roles.accent), 'measured', (v) => {
    style.color.accent = v
  })
  put('color.border', normalizeHex(roles.border), 'measured', (v) => {
    style.color.border = v
  })

  const typo = bs.typography ?? {}
  const headingSizePx = pxValue(typo.headingSize)
  const faces: StyleFontFace[] = (bs.fontFaces ?? []).map((f) => ({ ...f }))

  // NOT taken at its word, and cross-checked against the `@font-face` list instead. MEASURED (2026-08-13):
  // elevenlabs.io imported `display: 'Goudy Mediaeval Fallback'`, `body: 'Antoine Drop Caps Fallback'` off a
  // page that ships six real font files, three of them Waldenburg — the extractor had read the site's
  // metric-adjustment declarations as its brand faces. Neither family can load, so the deck fell back to a
  // system font AND lost the brand.
  const display = resolveFamily(bs.headingFont, faces)
  const body = resolveFamily(bs.bodyFont, faces)
  put('type.display', display?.family, display?.repaired ? 'inferred' : 'measured', (v) => {
    style.type.display = v
  })
  put('type.body', body?.family, body?.repaired ? 'inferred' : 'measured', (v) => {
    style.type.body = v
  })
  // The weights follow the family they describe: when the display family is a repair, the weight list
  // attributed to it is a repair too, however exact each individual `@font-face` was.
  put(
    'type.displayWeights',
    shippedWeights(faces, display?.family),
    display?.repaired ? 'inferred' : 'measured',
    (v) => {
      style.type.displayWeights = v
    },
  )
  // Recorded VERBATIM as measured, in em. The clamp that keeps a brand's 16px-tuned tracking from crushing
  // a 120px title (`deckFlowPrompt.sanitizeHeadingLetterSpacing`) is a RENDER decision and belongs in
  // styleRender — the record's job is to say what the site actually does.
  put('type.tracking', cssTrackingEm(typo.headingLetterSpacing, headingSizePx), 'measured', (v) => {
    style.type.tracking = v
  })
  put('type.leading', cssLeading(typo.headingLineHeight, headingSizePx), 'measured', (v) => {
    style.type.leading = v
  })
  put('type.case', cssTextCase(typo.headingTransform), 'measured', (v) => {
    style.type.case = v
  })
  // NOT SET: `type.scale`. The CSS gives two sizes (heading, body), and a ratio between two sizes is not a
  // ratio between STEPS unless you also assume how many steps sit between them. That assumption is exactly
  // the kind of invented number that would outrank a preset's real scale.
  //
  // SET: `type.roles`, when the kit stored one. A stored scale was written by the panel, an agent op or a
  // site read, so it is a decision about THIS brand and `measured` is the honest stamp — it is not a
  // guess this importer made. Clamped on the way in because the bands are the contract's and the panel
  // is not (see `clampScaleToBands`). Nothing is derived here for a kit that stored none: that is
  // `styleFromBrandKit`'s job, because the derivation needs the profile's families as well as the CSS.
  put('type.roles', bs.typeScale ? clampScaleToBands(bs.typeScale) : undefined, 'measured', (v) => {
    style.type.roles = v
  })
  put('radius', pxValue(typo.radius), 'measured', (v) => {
    style.radius = { md: v }
  })

  // THE RHYTHM. `bs.spacing` is the string[] the backend works to extract and which nothing in production
  // reads; this is where that extraction finally pays for itself. MEASURED (2026-08-13): `space` was empty
  // for four of five brands and wrong for the fifth, and five brands then produced five RECOLOURINGS of one
  // deck — colour and logo varied, spacing and rhythm did not, because an empty field hands the decision to
  // a shared system prompt that answers it identically every time.
  const rhythm = readSpacingRhythm(bs.spacing ?? [])
  // `measured` when the step is a length the sheet literally declares; `inferred` when it is a divisor that
  // appears nowhere in it — [12,18,30] yields 6, which is a real base and still a number nobody wrote down.
  put('space.unit', rhythm.unit, rhythm.unitIsDeclared ? 'measured' : 'inferred', (v) => {
    style.space.unit = v
  })
  // REVERSED (2026-08-13) on evidence. This used to refuse density outright, arguing that a declared ramp
  // lists what a system SHIPS rather than what a page uses. Still true — and it left the field empty on every
  // real source, which is not the neutral outcome it looks like: an empty field is answered by the shared
  // prompt, the same way, for every brand. The ramp's SPREAD is real evidence, because a system whose
  // everyday gap is 4x its base was designed for different pages than one that tops out at 1.5x. A ratio is
  // not a measurement, so this lands `inferred` and a sentence describing actual air ties with it — in a
  // brand kit carrying both, `mergeStyles` gives the tie to the later argument and the scrape arrives last,
  // so the ramp wins there. The sentence still survives whole in `voice.guideline` either way.
  put('space.density', rhythm.density, 'inferred', (v) => {
    style.space.density = v
  })

  put('assets.logoUrl', trimmed(bs.logoUrl), 'measured', (v) => {
    style.assets.logoUrl = v
  })
  // NOT SET: `assets.logoOnLight`. Nothing in a BrandStyle describes the MARK's own contrast; the page
  // background is a fact about the page, not about the logo sitting on it.
  put('assets.fontFaces', faces.length > 0 ? faces : undefined, 'measured', (v) => {
    style.assets.fontFaces = v
  })

  // MEASURED is not the same as USABLE, and this is the source where the two come apart hardest: the
  // backend's role classifier reads a hex out of a real stylesheet and can still label a pale pink as the
  // page's text colour (granola.ai, 1.23:1 on white).
  repairColorRoles(style)
  return style
}

/**
 * The kit's KIT-WIDE standing rules as plain lines.
 *
 * Read defensively, because the profile is a JSON column: a record written by an older client, or
 * half-written by a failed patch, must cost its own line and nothing else. Deduped case-insensitively
 * because "add a rule" is the one operation a user repeats without noticing.
 *
 * SCOPED, and that filter is the point. The same list also holds rules learned on ONE composition
 * ("the metrics figure stays 240px"); those ride their own role's prompt block, not the whole kit's style
 * recipe. Promoting a metrics rule to kit scope here would silently apply it to every cover the user
 * generates, which is a preference they never stated. A bare string, or an entry with no legible scope, is
 * treated as kit-wide: that is what the writers default to.
 */
function profileRules(p: MoodboardProfile): string[] {
  const raw: unknown = p.rules
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const entry of raw as unknown[]) {
    const record = typeof entry === 'string' ? null : (entry as { text?: string; scope?: string } | null)
    const scope = record?.scope
    if (scope === 'composition' || scope === 'template') continue
    const text = trimmed(typeof entry === 'string' ? entry : (record?.text ?? undefined))
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(text)
  }
  return out
}

/** The kit's tone examples, same defensive read. `kind` is narrowed rather than trusted: an unknown kind
 *  would be printed as a label the model then treats as an instruction. */
function profileExamples(p: MoodboardProfile): StyleVoiceExample[] {
  const raw: unknown = p.toneExamples
  if (!Array.isArray(raw)) return []
  const out: StyleVoiceExample[] = []
  for (const item of raw as unknown[]) {
    const entry = (item ?? {}) as { kind?: string; slot?: string; text?: string; note?: string }
    const text = trimmed(entry.text)
    if (!text) continue
    const kind = entry.kind === 'do' || entry.kind === 'dont' ? entry.kind : 'sample'
    const slot = trimmed(entry.slot)
    const note = trimmed(entry.note)
    out.push({ kind, text, ...(slot ? { slot } : {}), ...(note ? { note } : {}) })
  }
  return out
}

/**
 * A MOODBOARD / reference-image synthesis → Style. Everything is `inferred`: a vision model read pictures.
 *
 * `from` is a parameter because the same profile shape is produced by four different acts — a moodboard,
 * one wired image, a deck-scoped design system, a saved kit — and which one it was is the thing the
 * playground's "why does it look like this" panel needs to say.
 *
 * This is where the prose gets mined. Every sentence we parse is ALSO kept verbatim in `voice.guideline`,
 * because a sentence always says more than the number in it ("~40px between blocks, tight inside groups"
 * yields a 40px step and loses the scoping).
 */
export function styleFromMoodboardProfile(
  p: MoodboardProfile,
  from: StyleSource,
  opts?: { name?: string; origin?: string },
): Style {
  const name = opts?.name ?? 'Reference style'
  const origin = opts?.origin ?? name
  const style = emptyStyle(`${from}:${slug(origin)}`, name)
  const put = writer(style, from, origin)

  const palette = unique(compact((p.palette ?? []).map((c) => normalizeHex(c))))
  put('color.palette', palette.length > 0 ? palette : undefined, 'inferred', (v) => {
    style.color.palette = v
  })
  const roles = p.roles ?? {}
  put('color.bg', normalizeHex(roles.background), 'inferred', (v) => {
    style.color.bg = v
  })
  put('color.surface', normalizeHex(roles.surface), 'inferred', (v) => {
    style.color.surface = v
  })
  put('color.text', normalizeHex(roles.text), 'inferred', (v) => {
    style.color.text = v
  })
  put('color.muted', normalizeHex(roles.textMuted), 'inferred', (v) => {
    style.color.muted = v
  })
  put('color.accent', normalizeHex(roles.accent), 'inferred', (v) => {
    style.color.accent = v
  })
  put('color.border', normalizeHex(roles.border), 'inferred', (v) => {
    style.color.border = v
  })

  // Same fallback guard as the website path, and it earns its place here too: a profile's fonts are often
  // CSS-extracted from a member's url by the same synthesis step, so a synthetic `<Name> Fallback` reaches
  // this shape as readily as it reaches a BrandStyle. There are no `@font-face` files to cross-check
  // against, so a repair here can only ever strip the marker — which is already all this source claims.
  const displayFamily = resolveFamily(p.headingFont, [])
  const bodyFamily = resolveFamily(p.bodyFont, [])
  put('type.display', displayFamily?.family, 'inferred', (v) => {
    style.type.display = v
  })
  put('type.body', bodyFamily?.family, 'inferred', (v) => {
    style.type.body = v
  })

  const typography = p.typography ?? ''
  put('type.case', firstKeyword(typography, CASE_WORDS), 'inferred', (v) => {
    style.type.case = v
  })
  put('type.tracking', proseTrackingEm(typography), 'inferred', (v) => {
    style.type.tracking = v
  })
  put('type.leading', proseLeading(typography), 'inferred', (v) => {
    style.type.leading = v
  })
  put('type.scale', proseTypeScale(typography), 'inferred', (v) => {
    style.type.scale = v
  })

  const spacing = p.spacing ?? ''
  put('space.unit', proseSpacingUnit(spacing), 'inferred', (v) => {
    style.space.unit = v
  })
  put('space.density', firstKeyword(spacing, DENSITY_WORDS), 'inferred', (v) => {
    style.space.density = v
  })

  const shadows = p.shadows ?? ''
  const depth = firstKeyword(shadows, DEPTH_WORDS)
  const values = literalShadowValues(shadows)
  // A quoted literal is kept even when no word names the depth, and the depth is NOT back-derived from the
  // numbers: "0 4px 0 #000" is hard and "0 2px 8px rgba(0,0,0,.08)" is soft, and the rule that separates
  // them is a judgement this file has no evidence for.
  const shadow = depth || values ? { ...(depth ? { depth } : {}), ...(values ? { values } : {}) } : undefined
  put('shadow', shadow, 'inferred', (v) => {
    style.shadow = v
  })

  const { patterns, unmatched } = matchLayoutPatterns(p.patterns ?? [])
  put('layout.patterns', patterns.length > 0 ? patterns : undefined, 'inferred', (v) => {
    style.layout = { patterns: v }
  })

  // Every prose field, kept whole — including the ones a parser above already mined, because a sentence
  // always says more than the number in it. The labels are the ones `buildMoodboardPromptBlock` already
  // uses, so a guideline assembled here reads like the block the model sees today.
  const guideline = joinLines([
    trimmed(p.guideline),
    trimmed(p.tasteProfile),
    labelled('Type system', typography),
    labelled('Spacing', spacing),
    labelled('Shadows', shadows),
    ...unmatched,
  ])
  // STANDING RULES, read STRAIGHT ACROSS. They never touch `matchLayoutPatterns` above and they never
  // enter the guideline: the matcher would read "never stack two adjectives in a headline" as a request for
  // the STACK family, and the guideline is clamped at 1,200 chars by the renderer, so a rule folded in
  // there is a rule that silently stops being enforced once the art direction gets long. Their own field,
  // their own recipe section, no parsing on the way.
  const rules = profileRules(p)
  const examples = profileExamples(p)
  const voice: StyleVoice = {
    tone: trimmed(p.tone),
    keywords: [...(p.keywords ?? [])],
    avoids: [...(p.avoids ?? [])],
    ...(rules.length ? { rules } : {}),
    ...(examples.length ? { examples } : {}),
    guideline,
  }
  const hasVoice = Boolean(
    voice.tone ||
      voice.guideline ||
      voice.keywords.length > 0 ||
      voice.avoids.length > 0 ||
      rules.length > 0 ||
      examples.length > 0,
  )
  put('voice', hasVoice ? voice : undefined, 'inferred', (v) => {
    style.voice = v
  })

  // A vision model naming roles off a moodboard misses contrast at least as often as a CSS classifier does
  // — it is matching what a picture FEELS like, and a picture has no computed style to check against.
  repairColorRoles(style)
  return style
}

/**
 * A BRAND KIT → Style. The kit is two sources in one object, and this is the function that stops them
 * fighting: the `MoodboardProfile` half is a vision estimate, the optional `brandStyle` half is the real
 * CSS of a real page.
 *
 * `moodboardToBrandTokens` spells that precedence out one field at a time (`exact?.palette?.length ? … :
 * profile?.palette`, and again for roles, and again for each font). Here it falls out of the confidence
 * ranks — measured beats inferred wherever the two happen to collide — so a field added to `Style` later
 * cannot silently forget to re-implement the rule.
 *
 * The measured half keeps `from: 'website'`, because that is where the measurement happened; the kit's
 * name rides along in `origin` so the panel can still say which kit the user picked. A kit carries no
 * marker for hand-typed fields, so nothing here claims `manual`; when the kit editor starts recording
 * edits, they enter as `{ from: 'manual', confidence: 'measured' }` and win on tie by arriving later.
 *
 * COLOUR IS THE ONE EXCEPTION TO THE RANKS, and it is the bug this function was rewritten for.
 * MEASURED (bankofgeorgia.ge, 2026-09-03): the kit holds nine curated colours with `#ff6714` as its
 * accent and no blue anywhere, and every generated cover came out with a BLUE headline. Both halves were
 * doing exactly what they were told. The Colors panel writes `profile` only (`projectColorGroups`), the
 * `brandStyle` half is the scrape stored whole when the kit was created from a URL and NOTHING ever
 * updates its colours again, and `measured` outranks `inferred` forever — so the scrape's blue beat nine
 * deliberate choices on every generation, and the panel was decorative.
 *
 * The rank is right for everything else the page states about itself. It is wrong for colour alone,
 * because these are not two readings of one fact: one is what a scraper found on a page ONCE, the other
 * is what a person has chosen SINCE. The person wins. Type, spacing, shadow, radius, font files and the
 * url keep taking the scrape, because for those the site really is the only evidence there is.
 *
 * NO MIGRATION IS NEEDED, by construction of the gate. `colorGroups` is seeded in memory from
 * roles+palette whenever a kit has none (`colorsOf`) and is only ever PERSISTED by a write, so a stored
 * profile carrying groups is a profile whose colours a human or the kit agent has already touched. Every
 * kit that was ever edited therefore passes the gate retroactively, and a kit straight off a URL that
 * nobody has opened has none — for that one the scrape is still the only colour evidence in the object,
 * so it still wins. That is deliberate, not a leftover.
 */
export function styleFromBrandKit(
  kit: BrandKit | StoredBrandKit,
  /**
   * `ground`: the background this document will be painted on, one of `color.grounds`. ACCEPTED AND NOT
   * YET APPLIED. Phase C of the kit-as-design-system plan (2026-09-05) adds `groundStyle`, which swaps
   * `color.bg` for it after `repairColorRoles` and lets `withStoredMark` pick the mark made for that
   * ground; the parameter is threaded now so the artifact resolver and the kit page can already say which
   * ground they mean without a signature change landing in three files later.
   */
  _opts?: { ground?: string },
): Style {
  const guessed = styleFromMoodboardProfile(kit.profile, 'brandKit', { name: kit.name, origin: kit.name })
  const resolved = kit.brandStyle ? mergedWithScrape(kit, guessed) : { ...guessed, id: kit.id }
  withKitTypeRoles(resolved, kit)
  withKitGrounds(resolved, kit)
  return withStoredMark(resolved, kit)
}

/** The kit's measured half merged over its guessed half, colour-audited. Split out of `styleFromBrandKit`
 *  only so both branches — with and without a stored `brandStyle` — run the same tail. */
function mergedWithScrape(kit: BrandKit | StoredBrandKit, guessed: Style): Style {
  const brandStyle = kit.brandStyle as BrandStyle
  const url = trimmed(brandStyle.url)
  const scraped = styleFromBrandStyle(brandStyle, {
    name: kit.name,
    origin: url ? `${kit.name} — ${url}` : kit.name,
  })
  const measured = hasCuratedColors(kit.profile) ? withoutColorClaimsAlreadyMade(scraped, guessed) : scraped
  // AUDITED AGAIN AFTER THE MERGE, and it is not belt-and-braces: each half was legible on its OWN
  // background, and the merge is free to take `bg` from one half and `text` from the other. Two compliant
  // sources can compose into an unreadable pair, and this is the only place that can see it happen.
  const merged = { ...mergeStyles(guessed, measured), id: kit.id, name: kit.name }
  repairColorRoles(merged)
  return merged
}

/**
 * THE FIVE SIZES, on every kit.
 *
 * Measured 2026-09-05: the recipe stated a type scale only when one was STORED, and a kit stores one only
 * once somebody edits the Typography panel — the panel itself derives a scale for display and never writes
 * it. So the panel showed "Display 160px" while the writer was told families and a ratio and invented its
 * own sizes, nine of them in one document. The same derivation the panel uses now runs here, so what the
 * page shows is what the document gets, and a kit nobody has typed into still hands over five numbers.
 *
 * `measured` when the kit stored the scale (a person or a read decided it), `default` when it fell out of
 * the anchors — and `default` is the honest rank: a wired site that measured its own ramp should beat it,
 * and `mergeStyles` will let it.
 *
 * Read off the PROFILE's families, not the scrape's, because those are the ones the panel snaps weights
 * against and a display face with one file must not be asked for a bold on either surface.
 */
function withKitTypeRoles(style: Style, kit: BrandKit | StoredBrandKit): void {
  const stored = kit.brandStyle?.typeScale
  const put = writer(style, 'brandKit', kit.name)
  put(
    'type.roles',
    typeScaleForKit({
      typeScale: stored ?? null,
      typography: kit.brandStyle?.typography ?? null,
      fontFaces: kit.brandStyle?.fontFaces ?? null,
      headingFont: kit.profile.headingFont ?? null,
      bodyFont: kit.profile.bodyFont ?? null,
    }),
    stored ? 'measured' : 'default',
    (v) => {
      style.type.roles = v
    },
  )
}

/**
 * EVERY GROUND the kit's Colors panel holds, primary first.
 *
 * `projectColorGroups` writes the first background into `roles.background` and flattens the rest into
 * `palette`, where a second ground is indistinguishable from a swatch — which is why a kit with a white
 * and a navy background could only ever generate on white. The groups still hold all of them, in the
 * order the panel shows them, and the first is the primary by the panel's own rule ("priority is
 * position").
 *
 * Read defensively off the JSON column, like {@link hasCuratedColors} and for the same reason: this file
 * is a leaf and will not import the kit editor's tree for one optional array. A kit with no groups falls
 * back to its one role background, so `grounds` is never empty for a kit that has a ground at all.
 */
function withKitGrounds(style: Style, kit: BrandKit | StoredBrandKit): void {
  const raw: unknown = (kit.profile as { colorGroups?: unknown }).colorGroups
  const fromGroups = Array.isArray(raw)
    ? unique(
        compact(
          (raw as unknown[]).map((entry) => {
            const record = (entry ?? {}) as { group?: unknown; value?: unknown }
            if (record.group !== 'background' || typeof record.value !== 'string') return undefined
            return normalizeHex(record.value)
          }),
        ),
      )
    : []
  const fallback = normalizeHex(kit.profile.roles?.background) ?? style.color.bg
  const grounds = fromGroups.length > 0 ? fromGroups : fallback ? [fallback] : undefined
  const put = writer(style, 'brandKit', kit.name)
  // `inferred`, whichever way it was read: the same curated colours enter as the profile's ROLES at that
  // rank (`styleFromMoodboardProfile`), and a list of them cannot outrank its own members. Nothing else
  // claims this field today, so the rank only matters for the day a site read does.
  put('color.grounds', grounds, 'inferred', (v) => {
    style.color.grounds = v
  })
}

/**
 * THE KIT'S MARK, off the column, which is the only place it exists.
 *
 * This is the join that was missing, and its absence is why no generated document has ever carried a
 * user's logo. `brandKitFromDto` deletes `brandStyle.logoUrl` on every kit it hydrates (`stripLogo`),
 * deliberately, so that the column cannot be shadowed by a stale copy — and `styleFromBrandStyle` is
 * the ONLY writer of `assets.logoUrl` in the tree, reading exactly the field that was just deleted. So
 * `style.assets.logoUrl` was always undefined, `recipeLogoLines` early-returned, the `## Logo` section
 * was never emitted, and the model was simply never told a logo exists. Every other part of the chain
 * was built and correct; the value never arrived. Guri, 2026-09-04: "I tried, and it does not add the
 * Deck logo."
 *
 * Applied AFTER the merge on purpose. A merge ranks two sources by confidence, and here there is only
 * one source that can ever hold the mark, so there is nothing to rank — running it through `mergeStyles`
 * would only give a future scrape a way to win against the file the user actually uploaded.
 *
 * `logoPlacement: 'OFF'` is the user saying not to put the mark on their slides, so it is honoured here
 * rather than downstream: a recipe that names a logo the product has been told to omit is an instruction
 * to place it.
 */
function withStoredMark(style: Style, kit: BrandKit | StoredBrandKit): Style {
  if (!('logoUrl' in kit)) return style
  const mark = kitMarkUrl(kit)
  if (!mark) return style
  // WHICH VARIATION, decided by the ground this theme actually paints. This is the whole point of the
  // variations and it was being thrown away: the mark came off `kit.logoUrl`, which is whichever card the
  // user last made ACTIVE, so a kit whose active card was the white-on-dark one handed a white mark to a
  // #eeeeee cover and the logo vanished. Guri, 2026-09-04: "there is the logo version in the background,
  // and in this kind of scenario, it should use this color. In that intro, as you see, it is purest white,
  // and it uses a white fucking logo."
  //
  // A variation is the mark MADE FOR a ground, so the ground picks it. The active card is only the
  // fallback, for a kit with no variation that suits — and then `recipeLogoLines` says so rather than
  // inventing a plate to rescue it.
  const chosen = markForGround(kit, style.color.bg) ?? { url: mark, onLight: kit.logoOnLight }
  const put = writer(style, 'brandKit', kit.name)
  // Both in ONE claim, because the record already treats them as one: `mergeStyles`'s
  // `claim('assets.logoUrl')` copies `logoOnLight` across with the URL. That pairing is right — a mark
  // and the ground it reads on are a single fact, and half of it is worse than neither. It is also why
  // there is no `assets.logoOnLight` StyleField to write to separately.
  //
  // Unlike a scraped mark this contrast HAS been judged: `logoOnLight` is set by the user clicking the
  // swatch their mark reads on, so the note in `styleFromBrandStyle` refusing to guess it does not apply.
  // Left undefined, `recipeLogoLines` falls back to "never sit it on the accent", and this kit knows
  // better than that.
  put('assets.logoUrl', chosen.url, 'measured', (v) => {
    style.assets.logoUrl = v
    if (typeof chosen.onLight === 'boolean') {
      style.assets.logoOnLight = chosen.onLight
    }
  })
  return style
}

/**
 * The stored variation whose own ground matches the one this document will paint, or null.
 *
 * Matched on TONE and not on the exact colour, deliberately: a mark drawn for #ffffff is the right mark
 * for #eeeeee and for #f7f7f7, and demanding an exact match would find nothing on almost every theme. A
 * variation's `background` is what the card behind it shows, which is precisely the ground its file was
 * made to sit on, so its tone is the whole answer.
 *
 * `var:<role>` grounds are resolved through the profile's own roles, because that is what the Logos panel
 * stores when a card points at a brand colour rather than a literal — an unresolvable one is skipped
 * rather than guessed at.
 */
function markForGround(
  kit: BrandKit | StoredBrandKit,
  bg: string | undefined,
): { url: string; onLight: boolean } | null {
  const variations = (kit.profile as { logoVariations?: { background?: string; logoUrl?: string }[] })
    .logoVariations
  if (!variations?.length || !bg) return null
  const wantLight = backgroundIsLight(bg, true)
  if (wantLight === undefined) return null

  const roles = (kit.profile as { roles?: Record<string, string> }).roles ?? {}
  for (const v of variations) {
    // Through `kitMarkUrl`, so the recipe names the mark in the SAME spelling the allow list and the
    // inline pass use. Handing back the raw stored value was a real bug and it cost three reports: the
    // recipe said `/api/v1/storage/...`, the inline pass looked for `http://host/api/v1/storage/...`,
    // `split` found nothing, and the document kept a live URL that cannot load on an opaque origin.
    const url = kitMarkUrl({ logoUrl: v.logoUrl, logoPlacement: 'EVERY_SLIDE' })
    if (!url) continue
    const raw = v.background?.trim()
    const ground = raw?.startsWith('var:') ? roles[raw.slice(4)] : raw
    const groundIsLight = backgroundIsLight(ground, true)
    // The mark on a light card is a dark mark, so it is the one that reads on light.
    if (groundIsLight === wantLight) return { url, onLight: groundIsLight }
  }
  return null
}

/**
 * Has a person (or the kit agent acting for one) curated this kit's colours?
 *
 * `profile.colorGroups` is the grouped list the Colors panel edits, and its PRESENCE is the whole signal:
 * `colorsOf` seeds groups in memory from the legacy roles+palette for a kit that has none, and only a real
 * edit ever writes them back. So a stored non-empty `colorGroups` means "these colours were chosen", and
 * an absent one means "nobody has looked".
 *
 * Read defensively off the JSON column exactly like {@link profileRules}, and NOT through
 * `kitAgent/kitEdits` even though that module owns the type: this file is a leaf whose only runtime import
 * is birtvyBrandTruth's colour maths, and a predicate over one optional array is not worth an edge into
 * the kit editor's tree.
 */
function hasCuratedColors(profile: MoodboardProfile): boolean {
  const raw: unknown = (profile as { colorGroups?: unknown }).colorGroups
  return Array.isArray(raw) && raw.length > 0
}

/**
 * The scrape with its claim dropped on exactly the colours the CURATED half already answers, and no
 * others.
 *
 * The first version dropped every `color.*` claim the moment a kit had any curated colours at all, and
 * that was worse than the bug it fixed. `mergeStyles` leaves a field nobody claims at the base half's
 * value, and the base half is the profile, so every role the curated groups do NOT fill came out
 * undefined instead of falling back to the scrape. A kit whose one swatch sits in the `other` group
 * ended up with a palette and nothing else: no bg, no text, no accent. `repairColorRoles` then has no
 * background to measure against and returns early, `artifactVars` emits no `--a-bg` and no `--a-text`,
 * and the document generates with no ground and no ink. One click reaches it: delete the last
 * background swatch, or run a Figma import whose variables all classify as `other`.
 *
 * So the rule is per FIELD, which is what "curated colour beats scraped colour" always meant. Wherever
 * the person has answered, their answer wins. Wherever they have not, the site is still the only colour
 * evidence in the object and still speaks.
 *
 * Dropping a claim is the whole edit; nothing here blanks a value. Testing the prefix rather than a
 * hand-listed six keeps a `color.*` field added to `StyleField` later inside the rule instead of quietly
 * reopening the hole.
 */
function withoutColorClaimsAlreadyMade(scraped: Style, curated: Style): Style {
  const provenance: Style['provenance'] = {}
  for (const [field, entry] of Object.entries(scraped.provenance)) {
    if (field.startsWith('color.') && curated.provenance[field as StyleField]) continue
    provenance[field as StyleField] = entry
  }
  return { ...scraped, provenance }
}

/**
 * An ARTIFACT's `:root` token block → Style. Input is what `birtvyOpen/artifactMeta.parseTokens` returns:
 * full property names (`--a-bg`) mapped to their declared values.
 *
 * This closes the gap the audit named: artifacts speak `--a-bg / --a-text / --a-accent` and brand kits
 * speak `roles.background / roles.text / roles.accent`, with zero translation between them, so a kit
 * could not reach an artifact and an artifact's look could not be saved as a kit. Both now land here.
 *
 * `manual` + `measured` is the honest pair: the tokens were typed (by a person or by a model authoring the
 * document), and reading them back is reading a real declaration, not a guess about one. A value that is
 * not a colour — `var(--brand)`, a gradient, `currentColor` — is refused rather than stored as a string
 * that later fails to parse somewhere less forgiving.
 */
export function styleFromArtifactTokens(tokens: Record<string, string>, opts?: { name?: string }): Style {
  const name = opts?.name ?? 'Artifact theme'
  const origin = `${name} (:root tokens)`
  const style = emptyStyle(`artifact:${slug(name)}`, name)
  const put = writer(style, 'manual', origin)
  const token = (key: string): string | undefined => {
    const raw: unknown = tokens[key]
    return typeof raw === 'string' ? trimmed(raw) : undefined
  }

  put('color.bg', normalizeHex(token('--a-bg')), 'measured', (v) => {
    style.color.bg = v
  })
  put('color.surface', normalizeHex(token('--a-surface')), 'measured', (v) => {
    style.color.surface = v
  })
  put('color.text', normalizeHex(token('--a-text')), 'measured', (v) => {
    style.color.text = v
  })
  put('color.muted', normalizeHex(token('--a-muted')), 'measured', (v) => {
    style.color.muted = v
  })
  put('color.accent', normalizeHex(token('--a-accent')), 'measured', (v) => {
    style.color.accent = v
  })
  put('color.border', normalizeHex(token('--a-border')), 'measured', (v) => {
    style.color.border = v
  })

  // "Most prominent first" in DECLARATION ORDER: `parseTokens` inserts keys as it walks the block, and a
  // JS object preserves string-key insertion order, so this is the author's own ordering. Any `--a-*` that
  // resolves to a colour counts — a document is free to declare more tokens than the contract's minimum.
  const palette = unique(
    compact(
      Object.entries(tokens)
        .filter(([key]) => key.startsWith('--a-'))
        .map(([, value]) => (typeof value === 'string' ? normalizeHex(value) : undefined)),
    ),
  )
  put('color.palette', palette.length > 0 ? palette : undefined, 'measured', (v) => {
    style.color.palette = v
  })

  // A stack is read down to its first family, then through the same fallback guard: a document themed from a
  // Next.js site can inherit `'__Inter_Fallback_e66fe9', sans-serif` as its display token, and a token that
  // names a metric-adjustment face is a token no browser can resolve to the brand.
  const artifactDisplay = resolveFamily(firstFontFamily(token('--a-font-display')), [])
  const artifactBody = resolveFamily(firstFontFamily(token('--a-font-body')), [])
  put('type.display', artifactDisplay?.family, artifactDisplay?.repaired ? 'inferred' : 'measured', (v) => {
    style.type.display = v
  })
  put('type.body', artifactBody?.family, artifactBody?.repaired ? 'inferred' : 'measured', (v) => {
    style.type.body = v
  })
  put('radius', pxValue(token('--a-radius')), 'measured', (v) => {
    style.radius = { md: v }
  })

  // Audited like every other source. A document is the one source whose tokens were already SHIPPED, so it
  // is tempting to read them back verbatim — but a theme lifted off an unreadable artifact and re-applied
  // spreads the fault to every document it touches. One rule, all six sources.
  repairColorRoles(style)
  return style
}

// --- provenance-checked writes ---------------------------------------------------------------------

/** Applies a value and stamps its provenance in one move. Every field in this file goes through it, so a
 *  value cannot physically land without a confidence — which is the failure mode the record was written to
 *  end. `undefined` is a no-op, so the refusals above cost nothing at the call site. */
type Put = <T>(field: StyleField, value: T | undefined, confidence: StyleConfidence, apply: (value: T) => void) => void

function writer(style: Style, from: StyleSource, origin: string): Put {
  return (field, value, confidence, apply) => {
    if (value === undefined) return
    apply(value)
    style.provenance[field] = { from, confidence, origin }
  }
}

// --- legibility: a role you cannot read is not a role ------------------------------------------------
//
// MEASURED (granola.ai, 2026-08-13). The scrape labelled `text: '#ffdef6'` — pale pink — against
// `bg: '#ffffff'`. The full role table, as contrast against that background:
//
//   surface #f7f7f2  1.07     text  #ffdef6  1.23  FAIL     muted  #72726e  4.83
//   accent  #5b6f00  5.65     border #47432a  9.98
//
// SLIDES SURVIVED IT AND ARTIFACTS DID NOT, which is the whole shape of the bug. A slide's colours pass
// through a model that quietly substitutes black when the text colour it was handed is unreadable. An
// artifact is a complete HTML document: it declares `--a-text: #ffdef6` in `:root` and every rule that
// says `color: var(--a-text)` obeys it literally. Same record, one consumer that silently repairs and one
// that does exactly what it is told — so the fault only ever showed up on artifacts, and looked like an
// artifact bug rather than a role bug.
//
// The repair therefore belongs HERE, at assignment, not in either renderer: it is the single point that
// all six sources pass through, and neither consumer has to know it happened.
//
// WHAT IS AND IS NOT JUDGED. `text`, `muted` and `accent` carry glyphs. `bg` is the ground everything is
// measured against and cannot be measured against itself. `surface` and `border` are SHAPES — a card
// tint and a hairline — and holding them to a text threshold would repaint every brand's card the colour
// of its ink, which is a worse deck than the one this section exists to fix.

/** WCAG 2.x AA for body text. `muted` is held to the SAME bar as `text` on purpose: it carries real copy —
 *  captions, labels, sub-heads, the second line of a stat — and calling that decoration is how a brand ends
 *  up shipping grey-on-grey. */
const TEXT_MIN_RATIO = 4.5

/** WCAG 2.x AA for large text (≥24px, or ≥18.66px bold). An accent lands on headings, numbers and single
 *  emphasised words, all of them display-sized. Holding an accent to 4.5 would drag every brand colour
 *  toward black and delete the one field whose job is to be a colour. */
const ACCENT_MIN_RATIO = 3

/** Two decimals, because that is what every contrast checker reports and therefore what a designer will
 *  compare us against. It is also the precision the audit and the repair SHARE, so a repaired role can
 *  never come back reading "4.50 · FAIL" — one predicate, one answer. */
const RATIO_PRECISION = 2

/** Farthest apart two colours can be in character: opposite hues. Also what a neutral scores against a
 *  chromatic colour, since "grey or not grey" is a bigger difference than any angle between two hues. */
const HUE_MAX_DISTANCE = 180

/** OKLab lightness step when no declared colour can do the job and one has to be derived. 0.005 is ~200
 *  steps across the whole range: fine enough that the result lands just past the threshold instead of
 *  overshooting into black, cheap enough that the worst case is 200 luminance computations. */
const DERIVE_L_STEP = 0.005

/**
 * How a failing role is replaced once the candidates are down to the ones that clear its bar.
 *
 * `widest` takes the most legible; `narrowest` takes the least contrast that still passes. The difference
 * is the roles' own jobs, not a preference:
 *
 *   - `text` is the page's ink. `narrowest`, REVERSED on measurement (2026-08-13): it was `widest`, and on
 *     the real granola.ai scrape — the one sitting in nine saved lab sessions, palette `[#5b6f00, #ffffff,
 *     #f7f7f2, #ffdef6, #72726e, #47432a, #000000, #292929]` — `widest` repaired the pale pink to `#000000`
 *     at 21:1. That is precisely the failure this whole section exists to end: the model's silent black,
 *     now produced by us. Three neutrals qualified (#47432a 9.98, #292929 14.55, #000000 21.00), hue cannot
 *     separate neutrals, so the widest margin is always the palette's blackest colour — and virtually every
 *     scrape contains a black. `narrowest` takes #47432a, granola's own darkest tone, which is what the
 *     comments here and the unit tests already claimed the code did. What actually keeps `text` clear of
 *     `muted` is `reserved` in {@link legibleReplacement}, not the margin; the margin was never doing that job.
 *   - `muted` is SUBORDINATE copy. Repairing it to the most contrasty brand colour would make it identical
 *     to `text` — legible, and no longer muted. It wants the lightest value that still reads.
 *   - `accent` is a colour doing colour's work. Widening its margin means marching it toward black.
 */
type RoleMargin = 'widest' | 'narrowest'

interface RolePolicy {
  role: 'text' | 'muted' | 'accent'
  field: StyleField
  min: number
  margin: RoleMargin
}

/** Judged in this order, which is also the order {@link auditRoles} reports and the order a repair runs:
 *  ink first, then the copy under it, then the colour on top. The order is load-bearing for the repair —
 *  `text` settles before `muted` is judged, so a repaired `muted` steps around the value `text` just took
 *  rather than the one it is about to lose. */
const ROLE_POLICIES: readonly RolePolicy[] = [
  { role: 'text', field: 'color.text', min: TEXT_MIN_RATIO, margin: 'narrowest' },
  { role: 'muted', field: 'color.muted', min: TEXT_MIN_RATIO, margin: 'narrowest' },
  { role: 'accent', field: 'color.accent', min: ACCENT_MIN_RATIO, margin: 'narrowest' },
]

/** One role, measured against the style's own background. */
export interface RoleAudit {
  /** The `StyleColor` key — `text`, `muted`, `accent`. */
  role: string
  value: string
  /** Contrast against `color.bg`, to two decimals. */
  ratio: number
  passes: boolean
}

/**
 * WCAG 2.x contrast ratio between two colours, 1–21. Order-independent.
 *
 * Deliberately NOT an OKLab distance, even though the rest of this section's colour work is OKLab.
 * Perceptual distance says how DIFFERENT two colours look; this says whether a glyph edge resolves
 * against a ground, which is a photometric question about luminance and the number a designer, an
 * auditor and a browser devtools panel will all quote back at us.
 *
 * A colour that cannot be parsed scores 1 — no contrast — so an unreadable input can never make a role
 * pass. Failing open here would put the pale pink straight back.
 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la === undefined || lb === undefined) return 1
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Every text-carrying role, measured against `color.bg`.
 *
 * Returns [] when the style names no background: a ratio needs two colours, and inventing the missing one
 * would report a verdict about a page nobody has described. An absent `bg` is already a gap `styleGaps`
 * reports and a preset fills — and that preset's own roles get audited when it lands.
 *
 * `surface` and `border` are absent from this list ON PURPOSE, not by oversight. They are exempt from the
 * thresholds, so reporting them next to a `passes` flag would invite exactly the "fix" that ruins them.
 */
export function auditRoles(style: Style): RoleAudit[] {
  const bg = style.color.bg
  if (!bg) return []
  const out: RoleAudit[] = []
  for (const policy of ROLE_POLICIES) {
    const value = style.color[policy.role]
    if (!value) continue
    const ratio = ratioOf(value, bg)
    out.push({ role: policy.role, value, ratio, passes: ratio >= policy.min })
  }
  return out
}

/**
 * Audit, then repair in place. Called at the end of every importer.
 *
 * A repaired role drops to `inferred` — the source did not state this value, we worked it out — and its
 * origin becomes the receipt: `repaired from #ffdef6 — 1.2:1 on bg`. Both halves matter. The confidence
 * drop means a later measured source can still overrule us, which is right, because we guessed. The
 * receipt means the inspector can show WHY a brand colour moved; a silent swap of someone's brand colour
 * is not a fix, it is a second bug wearing the first one's clothes.
 *
 * When nothing can be made to work — a saturated hue on a mid-tone ground, where no lightness reaches the
 * threshold before the colour clips out of sRGB — the role is LEFT EXACTLY AS IT WAS. `auditRoles` then
 * reports it failing, which is a fact a caller can act on. Swapping in something that also fails would
 * destroy the brand's colour and fix nothing.
 */
function repairColorRoles(style: Style): void {
  const bg = style.color.bg
  if (!bg) return // nothing to be legible AGAINST — see auditRoles
  for (const policy of ROLE_POLICIES) {
    const original = style.color[policy.role]
    if (!original) continue
    const ratio = ratioOf(original, bg)
    if (ratio >= policy.min) continue

    const repaired = legibleReplacement(original, bg, style, policy)
    if (!repaired || repaired === original) continue

    style.color[policy.role] = repaired
    const prior = style.provenance[policy.field]
    style.provenance[policy.field] = {
      // `prior` is always present in practice — every role reaches here through `put`, which cannot land a
      // value without stamping one. The fallback exists so a repair can never leave a field unattributed.
      from: prior?.from ?? 'manual',
      confidence: 'inferred',
      origin: `repaired from ${original} — ${ratio.toFixed(1)}:1 on bg`,
    }
  }
}

/**
 * The colour a failing role becomes.
 *
 * DECLARED COLOURS FIRST, always. A colour the source itself named is not an invention, and a brand keeps
 * its character far better by borrowing its own dark tone than by having one computed for it. Only when
 * nothing the brand declared clears the bar is a value derived, and then from the ORIGINAL, so the repair
 * is still recognisably the colour it replaced.
 *
 * A repair MAY NOT LAND ON ANOTHER AUDITED ROLE'S COLOUR. Those three read as a hierarchy — ink, the
 * quieter copy under it, the colour on top — and collapsing two of them onto one value trades a
 * contrast bug for a flat page. MEASURED: an artifact declaring `--a-muted: #71717a` on `--a-bg: #0b0b0c`
 * is 4.07:1, just under the bar, and the only same-family colour the document declared that clears it is
 * `--a-text` itself. Excluding it sends the repair to derivation, which lifts that exact grey to #787881
 * — still muted, still subordinate, and now readable. Borrowing `text` would have deleted the distinction
 * the two tokens exist to carry. Roles OUTSIDE the family are fair game: granola's ink repairs to its own
 * `border` tone, and ink sharing a value with a hairline costs nothing.
 */
function legibleReplacement(original: string, bg: string, style: Style, policy: RolePolicy): string | undefined {
  const reserved = new Set(
    compact(ROLE_POLICIES.filter((p) => p.role !== policy.role).map((p) => style.color[p.role])),
  )
  const qualified = declaredColors(style).filter((hex) => !reserved.has(hex) && ratioOf(hex, bg) >= policy.min)
  const nearest = rankByCharacter(qualified, original, bg, policy)
  return nearest ?? deriveLegibleColor(original, bg, policy.min)
}

/**
 * Every colour this style declares, palette first.
 *
 * The palette is the ranked list and the obvious pool — but it is not the whole pool, because several
 * importers assign a role a colour that never entered it (a `MoodboardProfile` routinely names an accent
 * absent from its own palette). A colour the source named for another role is still a colour the brand
 * chose, so it is a legitimate candidate; the background and any failing role simply lose on the
 * threshold filter. Order is palette-then-roles and deduped, so ties break toward prominence.
 */
function declaredColors(style: Style): string[] {
  const c = style.color
  return unique(compact([...c.palette, c.bg, c.surface, c.text, c.muted, c.accent, c.border]))
}

/**
 * The qualified candidate that best preserves the brand's character.
 *
 * Hue distance first — that is what "the brand survives" means in a colour, and the whole reason for
 * preferring a declared colour over a computed one. Margin second, per the role's own job (see
 * {@link RoleMargin}). Candidate order last, so the result is deterministic and prominence wins a true
 * tie.
 */
function rankByCharacter(
  candidates: readonly string[],
  original: string,
  bg: string,
  policy: RolePolicy,
): string | undefined {
  const scored = candidates.map((hex, index) => ({
    hex,
    index,
    hue: hueDistance(original, hex),
    ratio: ratioOf(hex, bg),
  }))
  scored.sort(
    (a, b) =>
      a.hue - b.hue ||
      (policy.margin === 'widest' ? b.ratio - a.ratio : a.ratio - b.ratio) ||
      a.index - b.index,
  )
  return scored[0]?.hex
}

/**
 * How far apart two colours are in CHARACTER, in degrees.
 *
 * Two neutrals are the same family whatever their hue angle says — the angle of a near-grey is numerical
 * noise, and a page's black, its slate and its off-white are all "the neutral". A neutral against a
 * chromatic colour scores the maximum, because grey-or-not-grey is a bigger change than any angle between
 * two hues: it is what stops a brand's pale pink being repaired into its olive accent.
 *
 * `isNeutralHex` is birtvyBrandTruth's measured chroma cut (0.050), calibrated on real brand palettes
 * rather than picked round. THE GRANOLA CASE SITS RIGHT AT IT: `#ffdef6` has OKLab chroma 0.048, just
 * inside the neutral line, which is precisely why it repairs to a neutral (`#47432a`, 9.98:1) rather than
 * to the only chromatic colour that would have qualified (`#5b6f00`, an olive green). Borderline, and the
 * right side of the border: at 1.23:1 that pink was never carrying any hue a reader could see.
 */
function hueDistance(a: string, b: string): number {
  const neutralA = isNeutralHex(a)
  const neutralB = isNeutralHex(b)
  if (neutralA && neutralB) return 0
  if (neutralA !== neutralB) return HUE_MAX_DISTANCE
  const labA = oklabOfHex(a)
  const labB = oklabOfHex(b)
  if (!labA || !labB) return HUE_MAX_DISTANCE
  const diff = Math.abs(hueDegrees(labA) - hueDegrees(labB)) % 360
  return diff > HUE_MAX_DISTANCE ? 360 - diff : diff
}

function hueDegrees(lab: Oklab): number {
  return (Math.atan2(lab.b, lab.a) * 180) / Math.PI
}

/**
 * The original colour, pushed along OKLab lightness until it reads. THE LAST RESORT.
 *
 * Only L moves; `a` and `b` are held, which is what keeps the result the same colour — granola's
 * `#ffdef6` (hue 336°) comes back as `#8b6e84` (hue 335°), a mauve that still belongs to that brand.
 * OKLab rather than HSL because HSL's "lightness" is not lightness: darkening a yellow and a blue by the
 * same HSL amount changes their contrast by wildly different factors, so the same push would overshoot on
 * one hue and undershoot on the next.
 *
 * BOTH DIRECTIONS ARE TRIED and the nearer one wins, because which way is legible depends on the ground,
 * not on the colour: on a white page ink goes down, on a black one it goes up, and near a mid-tone ground
 * both work and the smaller move is the smaller lie.
 *
 * Each step is measured on the CLAMPED sRGB result rather than on the theoretical OKLab value. Holding
 * chroma while lightness moves eventually leaves the sRGB gamut, and the clamp that brings it back changes
 * the colour — sometimes, at the far edge, changing the hue as well. Testing what actually renders is the
 * only way the answer is honest; a saturated hue on a mid-tone ground can leave the gamut before it ever
 * reaches the threshold, and that is the case that correctly returns undefined.
 */
function deriveLegibleColor(original: string, bg: string, min: number): string | undefined {
  const lab = oklabOfHex(original)
  if (!lab) return undefined
  const darker = pushLightness(lab, bg, min, -DERIVE_L_STEP)
  const lighter = pushLightness(lab, bg, min, DERIVE_L_STEP)
  if (!darker) return lighter?.hex
  if (!lighter) return darker.hex
  return darker.steps <= lighter.steps ? darker.hex : lighter.hex
}

/** Walk L one direction until the rendered colour clears `min`, or until L runs off the end of the range.
 *  Returns the step count so the caller can take the smaller of the two moves. */
function pushLightness(
  lab: Oklab,
  bg: string,
  min: number,
  step: number,
): { hex: string; steps: number } | undefined {
  const maxSteps = Math.ceil(1 / Math.abs(step))
  for (let i = 1; i <= maxSteps; i += 1) {
    const raw = lab.L + step * i
    const L = Math.min(1, Math.max(0, raw))
    const hex = oklabToHex({ L, a: lab.a, b: lab.b })
    if (ratioOf(hex, bg) >= min) return { hex, steps: i }
    // The clamp fired, so this WAS the extreme of the range and it still failed. Nothing further in this
    // direction exists to try.
    if (L !== raw) return undefined
  }
  return undefined
}

/** OKLab → `#rrggbb`. The inverse of birtvyBrandTruth's `rgbToOklab`, which that file has no need for and
 *  which lives here rather than there because birtvy/ is not this layer's to edit. Out-of-gamut channels
 *  are clipped in LINEAR light, before the transfer function, so a clipped colour keeps as much of its
 *  relationship to its neighbours as a naive clip can. */
function oklabToHex(lab: Oklab): string {
  const l = (lab.L + 0.3963377774 * lab.a + 0.2158037573 * lab.b) ** 3
  const m = (lab.L - 0.1055613458 * lab.a - 0.0638541728 * lab.b) ** 3
  const s = (lab.L - 0.0894841775 * lab.a - 1.291485548 * lab.b) ** 3
  return rgbToHex(
    linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  )
}

/** Linear light → an sRGB code value in 0–255, gamut-clipped. */
function linearToSrgb(value: number): number {
  const c = Math.min(1, Math.max(0, value))
  return (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055) * 255
}

/** WCAG relative luminance. The transfer function is duplicated from birtvyBrandTruth, where it is
 *  module-private; the coefficients are WCAG's own (Rec.709 luma), NOT OKLab's L, which is a perceptual
 *  lightness and would give a different — and non-standard — ratio. */
function relativeLuminance(hex: string): number | undefined {
  const rgb = parseHexTriplet(hex)
  if (!rgb) return undefined
  const channel = (c: number) => {
    const v = c / 255
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2])
}

/** `normalizeHex` already brings every stored colour to `#rrggbb`, but `contrastRatio` is exported and a
 *  caller may hand it anything a stylesheet contains, so this accepts the same notations the record does. */
function parseHexTriplet(raw: string): [number, number, number] | undefined {
  const hex = normalizeHex(raw)
  if (!hex) return undefined
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** The shared, rounded predicate — see {@link RATIO_PRECISION}. */
function ratioOf(fg: string, bg: string): number {
  return round(contrastRatio(fg, bg), RATIO_PRECISION)
}

// --- prose → tokens --------------------------------------------------------------------------------
//
// The old model stored spacing, shadows, typography and layout as English sentences a vision model wrote,
// and the generator re-interpreted them on every run — which is where run-to-run inconsistency comes from.
// These parsers take the number or the enum ONLY when the sentence states one outright.
//
// Refusing is the feature. `styleGaps` reports an absent field and a preset fills exactly those; a wrong
// number sits at `inferred`, outranks the preset's `default` forever, and nothing ever flags it.

/** A keyword table: the value a sentence earns, and the phrases that earn it. Matched as substrings of the
 *  lowercased sentence, and the EARLIEST match wins — prose leads with its headline judgement and
 *  qualifies afterwards, so "Generous whitespace, ~40px between blocks, tight inside groups" is an airy
 *  layout that tightens inside groups, not a tight one. */
type KeywordTable<T> = ReadonlyArray<readonly [T, readonly string[]]>

function firstKeyword<T>(sentence: string, table: KeywordTable<T>): T | undefined {
  const text = sentence.toLowerCase()
  let best: { at: number; value: T } | undefined
  for (const [value, phrases] of table) {
    for (const phrase of phrases) {
      const at = text.indexOf(phrase)
      if (at >= 0 && (!best || at < best.at)) best = { at, value }
    }
  }
  return best?.value
}

/** Perceived air. Only words that describe how the layout BREATHES; "whitespace" alone is not here,
 *  because it appears in sentences arguing for both. */
const DENSITY_WORDS: KeywordTable<StyleDensity> = [
  ['airy', ['generous', 'airy', 'spacious', 'roomy', 'breathing room', 'plenty of space']],
  ['tight', ['tight', 'dense', 'compact', 'packed', 'snug', 'cramped']],
  ['normal', ['balanced', 'moderate', 'comfortable', 'even rhythm']],
]

/** Shadow language. `none` is a design position, so "Flat, no shadows" is a match, not a gap. */
const DEPTH_WORDS: KeywordTable<StyleDepth> = [
  ['none', ['no shadow', 'no drop shadow', 'without shadow', 'shadowless', 'flat']],
  ['soft', ['soft', 'subtle', 'gentle', 'diffuse', 'faint', 'light elevation']],
  ['hard', ['hard', 'sharp', 'offset shadow', 'crisp shadow', 'brutal']],
]

/** `StyleType.case` only has `none` and `upper`, so "lowercase" and "title case" are deliberately absent:
 *  neither is representable, and mapping them to `none` would assert something the sentence never said. */
const CASE_WORDS: KeywordTable<'none' | 'upper'> = [
  ['upper', ['uppercase', 'all caps', 'all-caps', 'set in caps']],
  ['none', ['sentence case', 'mixed case', 'no caps']],
]

/** The classical scale ratios, which are exact vocabulary rather than a guess: a typography sentence that
 *  says "major third" has named 1.25 as surely as if it had written the number. */
const NAMED_RATIOS: KeywordTable<number> = [
  [1.2, ['minor third']],
  [1.25, ['major third']],
  [1.333, ['perfect fourth']],
  [1.414, ['augmented fourth']],
  [1.5, ['perfect fifth']],
  [1.618, ['golden ratio']],
]

/**
 * Layout patterns from art-direction sentences.
 *
 * `LayoutPattern` is the vocabulary Deck's own slide judgment already uses, so a match here is a claim the
 * scorer can check. One sentence yields at most ONE pattern — the earliest named — because a sentence that
 * mentions a grid inside a split is describing one structure, and counting both would hand the generator a
 * preference list it never earned. Two confident matches beat six guesses.
 */
const PATTERN_WORDS: KeywordTable<LayoutPattern> = [
  ['split', ['split', 'two column', 'two-column', 'side by side', 'side-by-side', 'diptych', 'two halves']],
  ['spectrum', ['spectrum', 'continuum', 'ordered range', 'along a range']],
  ['field', ['scatter', 'field of', 'in a field']],
  ['orbit', ['orbit', 'hub and spoke', 'satellite', 'radial arrangement']],
  ['ledger', ['ledger', 'aligned rows', 'rows of', 'line items', 'table of']],
  ['single-number', ['single number', 'one number', 'big number', 'one big figure', 'hero stat', 'single stat']],
  ['stack', ['stack', 'vertical rhythm', 'single column', 'one column']],
  ['grid', ['grid', 'tiles', 'modular grid']],
  ['full-bleed', ['full bleed', 'full-bleed', 'edge to edge', 'edge-to-edge']],
]

function matchLayoutPatterns(sentences: readonly string[]): { patterns: LayoutPattern[]; unmatched: string[] } {
  const patterns: LayoutPattern[] = []
  const unmatched: string[] = []
  for (const sentence of sentences) {
    const text = trimmed(sentence)
    if (!text) continue
    const match = firstKeyword(text, PATTERN_WORDS)
    if (!match) {
      // Not a layout claim — a colour rule, a chrome rule, a don't. It still matters, so it survives in
      // voice.guideline rather than being dropped for failing to be a pattern.
      unmatched.push(text)
      continue
    }
    if (!patterns.includes(match)) patterns.push(match)
  }
  return { patterns, unmatched }
}

/** The base step a spacing sentence states: the SMALLEST px number in it. `space.unit` is the step
 *  everything else is a multiple of, so when a sentence names several ("120px margins, 24px inside cards")
 *  the smallest is the one the others are built from. Refuses without a px number — "tight inside groups"
 *  describes a feel, and "40" without a unit could be anything. */
function proseSpacingUnit(sentence: string): number | undefined {
  const found: number[] = []
  const re = /(\d*\.?\d+)\s*px/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(sentence)) !== null) {
    const value = Math.round(Number(m[1]))
    if (Number.isFinite(value) && value >= MIN_STEP_PX && value <= SPACE_MAX_PX) found.push(value)
  }
  if (found.length === 0) return undefined
  return Math.min(...found)
}

/** Letter-spacing from prose. The number must be LABELLED — an em or % floating in a typography sentence
 *  is as likely to be a line-height as a tracking, and "tight tracking" states no number at all, so it is
 *  refused and survives whole in voice.guideline. */
function proseTrackingEm(sentence: string): number | undefined {
  // The gap is LAZY: a greedy `\D` run swallows the minus sign of "-0.02em" and the value comes back
  // positive — a tracking with the wrong sign is worse than no tracking at all.
  const m = /(?:letter[\s-]?spacing|tracking)\D{0,24}?(-?\d*\.?\d+\s*(?:em|rem|px|%))/i.exec(sentence)
  if (!m) return undefined
  // No heading size in prose, so a px tracking cannot be converted into the record's em and is refused.
  return cssTrackingEm(m[1], undefined)
}

/** Line-height from prose, same labelled rule. A bare multiplier is accepted here because line-height is
 *  the one typographic value CSS itself writes without a unit. */
function proseLeading(sentence: string): number | undefined {
  const m = /(?:line[\s-]?height|leading)\D{0,24}?(\d*\.?\d+\s*(?:em|px|%)?)/i.exec(sentence)
  if (!m) return undefined
  return cssLeading(m[1], undefined)
}

/**
 * The type scale ratio, when the sentence names one.
 *
 * REFUSAL THAT MATTERS: "Display headlines at 3x body" is a display-to-body ratio, not a ratio between
 * STEPS — turning it into `scale: 3` would blow every intermediate size up by 3× wherever a renderer
 * walks the scale. A number only counts when it sits next to the word "scale", or when the sentence uses
 * the classical name for a ratio.
 */
function proseTypeScale(sentence: string): number | undefined {
  // Only whitespace and a connective may sit between the word and the number, so "scale is airy, 2
  // columns" cannot donate its 2.
  const stated =
    /(?:type\s+scale|modular\s+scale|scale)\s*(?:of|at|:|=)?\s*(\d*\.?\d+)/i.exec(sentence) ??
    /(\d*\.?\d+)\s*(?:x\s*)?(?:type\s+|modular\s+)?scale/i.exec(sentence)
  if (stated) {
    const value = Number(stated[1])
    if (Number.isFinite(value) && value >= SCALE_MIN && value <= SCALE_MAX) return value
  }
  return firstKeyword(sentence, NAMED_RATIOS)
}

/** Literal `box-shadow` values a sentence quotes ("Soft 0 2px 8px rgba(0,0,0,.08)"). Requires a colour at
 *  the end, which is what separates a shadow from two spacing numbers standing next to each other. */
function literalShadowValues(sentence: string): string[] | undefined {
  const re = /(?:-?\d*\.?\d+(?:px)?\s+){2,4}(?:rgba?\([^)]*\)|#[0-9a-f]{3,8})/gi
  const found = unique((sentence.match(re) ?? []).map((v) => v.trim().replace(/\s+/g, ' ')))
  return found.length > 0 ? found : undefined
}

// --- CSS values ------------------------------------------------------------------------------------

/** A CSS `<length>` in px. Accepts `24px` and a bare `0` (legal CSS, and the only unitless length).
 *  Everything else — `1em`, `50%`, `auto`, `var(--x)` — needs a context this module deliberately does not
 *  have, so it is refused. */
function pxValue(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const s = raw.trim().toLowerCase()
  if (/^0(?:px)?$/.test(s)) return 0
  const m = /^(-?\d*\.?\d+)px$/.exec(s)
  if (!m) return undefined
  const value = Number(m[1])
  return Number.isFinite(value) ? value : undefined
}

/** A spacing-scale entry in px. Adds rem to `pxValue` because spacing scales are written in rem more often
 *  than not; see REM_PX for the assumption that buys. */
function spacingPx(raw: string): number | undefined {
  const s = raw.trim().toLowerCase()
  const px = pxValue(s)
  if (px !== undefined) return px
  const m = /^(-?\d*\.?\d+)rem$/.exec(s)
  if (!m) return undefined
  const value = Number(m[1]) * REM_PX
  return Number.isFinite(value) ? value : undefined
}

/** What a declared spacing ramp says about a site's rhythm. Both fields are optional and independently
 *  refusable: a ramp can be solid enough to name a step and too short to describe air. */
interface SpacingRhythm {
  unit?: number
  /** True when `unit` is a length the sheet literally declares, which is what separates a measured step from
   *  a computed divisor nobody wrote down. Drives the confidence stamp, not the value. */
  unitIsDeclared: boolean
  density?: StyleDensity
}

/**
 * `BrandStyle.spacing` (a string[] of CSS values, read by nothing in production) → the base step and the
 * density. THE FIELD THAT MAKES SPACING STOP BEING IDENTICAL BETWEEN BRANDS.
 *
 * HAIRLINES COME OUT FIRST. Everything below {@link HAIRLINE_MAX_PX} is a border or an optical nudge, and
 * leaving it in caps the arithmetic at its own value — the measured `unit: 4` on granola.ai is exactly that.
 * The exception is a ramp made ENTIRELY of small values: there they are not hairlines among steps, they are
 * the only evidence there is, and the guard below still refuses anything under {@link MIN_STEP_PX}.
 *
 * THEN THE STEP, in the order the evidence deserves:
 *
 *   1. The gcd, when it lands at or above the hairline line. A scale is a set of multiples of one step, so
 *      their greatest common divisor IS that step: [8,16,24,40] → 8, [16,24,32,48] → 8.
 *   2. Otherwise the LARGEST DECLARED value that divides two thirds of the ramp. Below the hairline line a
 *      gcd is usually one odd value dragging the whole set down — [8,12,16,24,32,48] has a gcd of 4 because
 *      of its single 12, while 8 explains five of its six values and is a length the sheet actually states.
 *      This is also what recovers a hand-tuned page: in [13,15,15] the repeated 15 divides two of three.
 *   3. Otherwise the gcd anyway, if it clears {@link MIN_STEP_PX} — [12,18,30] → 6 is a real base even
 *      though no rung of that ladder is 6, and it is honestly labelled `inferred` for exactly that reason.
 *   4. Otherwise refuse. A gcd of 1 or 2 is not a step, it is the arithmetic saying there is no grid; an
 *      absent unit is a gap a preset fills, a fabricated one outranks that preset forever.
 *
 * A single value is refused outright: one number is a value, not a rhythm. So is one dominant value with
 * hairlines under it — the ramp is too sparse to be sure, and being sure is the whole point.
 */
function readSpacingRhythm(values: readonly string[]): SpacingRhythm {
  const all = values
    .map((v) => spacingPx(v))
    .filter((v): v is number => v !== undefined && v >= SPACE_MIN_PX && v <= SPACE_MAX_PX)
    .map((v) => Math.round(v))
    .sort((a, b) => a - b)

  // NOT the greatest common divisor, and that is deliberate — it was tried and reverted on 2026-08-13.
  //
  // An adversarial pass called the hairline strip a bug because a Tailwind ramp [4,8,12,16,24,32,48] has a
  // gcd of 4 and this returns 8. Switching to the gcd does satisfy the field's old wording, and it also
  // turns granola.ai's [4,12,16,24] back into `unit: 4` — the exact number Guri flagged as wrong, because a
  // deck built on a 4px step reads cramped. The atomic divisor of a ramp is not the rhythm a designer works
  // to: 4 is what 12 and 16 are BUILT from, 12 is what the page actually breathes at. The prompt needs the
  // second one. See the note on `StyleSpace.unit`, which now says which of the two this field is.
  const dominant = all.filter((v) => v >= HAIRLINE_MAX_PX)
  const ramp = dominant.length >= 2 ? dominant : dominant.length === 0 ? all : []
  if (ramp.length < 2) return { unitIsDeclared: false }

  const unit = baseStep(ramp)
  if (unit === undefined) return { unitIsDeclared: false }
  return { unit, unitIsDeclared: ramp.includes(unit), density: rampDensity(ramp, unit) }
}

/**
 * The ATOMS a site read yields, in the shape `BrandStyle` will store them (plan 2026-09-05, section 3):
 * the spacing step with its density, and the card radius. Written at kit creation and on every explicit
 * site read, so the numbers `styleFromBrandStyle` derives from the ramp are stored once as facts rather
 * than re-mined from `spacing[]` on every generation — and so a PDF read, which produces no ramp at all,
 * can write the same two fields from its own measurement. Absent keys stay absent: an empty `space` would
 * be read by a later importer as "measured nothing", which is a claim.
 */
export function atomsFromBrandStyle(bs: BrandStyle): {
  space?: { unit: number; density?: StyleDensity }
  radius?: { md: number }
} {
  const rhythm = readSpacingRhythm(bs.spacing ?? [])
  const radius = pxValue(bs.typography?.radius)
  return {
    ...(rhythm.unit !== undefined
      ? { space: { unit: rhythm.unit, ...(rhythm.density ? { density: rhythm.density } : {}) } }
      : {}),
    ...(radius !== undefined ? { radius: { md: radius } } : {}),
  }
}

/** Density from a gap-to-step ratio, the thresholds {@link rampDensity} uses. Exported so the PDF measure
 *  (a median gap over a measured step) lands on the same three words as a CSS ramp does. */
export function densityOfRatio(ratio: number): StyleDensity {
  if (ratio >= AIRY_RATIO) return 'airy'
  if (ratio <= TIGHT_RATIO) return 'tight'
  return 'normal'
}

/** The step of one already-cleaned ramp. See {@link readSpacingRhythm} for why the order is this order. */
function baseStep(ramp: readonly number[]): number | undefined {
  const divisor = ramp.reduce((a, b) => gcd(a, b))
  if (divisor >= HAIRLINE_MAX_PX) return divisor

  const needed = Math.ceil(ramp.length * STEP_MAJORITY)
  // Largest first: a ramp built on 16 is also divisible by 8, and the coarser step is the one the designer
  // was working to. Descending order means we take it before the finer one that merely also fits.
  const declared = [...new Set(ramp)].sort((a, b) => b - a)
  for (const candidate of declared) {
    if (candidate < MIN_STEP_PX) continue
    const explained = ramp.filter((v) => v % candidate === 0).length
    if (explained >= needed) return candidate
  }

  return divisor >= MIN_STEP_PX ? divisor : undefined
}

/**
 * Density from the ramp's own spread: how far the typical value sits from the base step.
 *
 * The MEDIAN rather than the maximum, because one 200px hero value would make every site airy, and the
 * median is what a page reaches for over and over. Under three values there is no median worth the name —
 * two values give their midpoint, which describes nothing — so density is refused there while the step
 * survives. Refusing one field and keeping the other is the point of `styleGaps`.
 */
function rampDensity(ramp: readonly number[], unit: number): StyleDensity | undefined {
  if (ramp.length < MIN_RAMP_FOR_DENSITY || unit <= 0) return undefined
  return densityOfRatio(median(ramp) / unit)
}

/** Median of an ASCENDING list. Even lengths average the two middles, which is what keeps a four-value ramp
 *  from reporting whichever of its middle pair happened to be indexed first. */
function median(sorted: readonly number[]): number {
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

/** A CSS letter-spacing as em, the record's unit. px and rem need the heading size to convert; without it
 *  they are refused rather than measured against an assumed 16px, because a wrong em is a crushed title.
 *  Out-of-range values (beyond ±0.5em) are a parse accident, not a brand decision. */
function cssTrackingEm(raw: string | undefined, headingSizePx: number | undefined): number | undefined {
  if (!raw) return undefined
  const m = /^(-?\d*\.?\d+)\s*(em|rem|px|%)$/.exec(raw.trim().toLowerCase())
  if (!m) return undefined
  const value = Number(m[1])
  if (!Number.isFinite(value)) return undefined
  const em =
    m[2] === 'em'
      ? value
      : m[2] === '%'
        ? value / 100
        : headingSizePx
          ? (m[2] === 'rem' ? value * REM_PX : value) / headingSizePx
          : undefined
  if (em === undefined) return undefined
  return em >= -0.5 && em <= 0.5 ? round(em, 4) : undefined
}

/** A CSS line-height as a multiplier. Unitless is CSS's own form and passes straight through; px converts
 *  only when the heading size is known. Outside 0.7–3 it is not a line-height. */
function cssLeading(raw: string | undefined, headingSizePx: number | undefined): number | undefined {
  if (!raw) return undefined
  const m = /^(\d*\.?\d+)\s*(em|px|%)?$/.exec(raw.trim().toLowerCase())
  if (!m) return undefined
  const value = Number(m[1])
  if (!Number.isFinite(value)) return undefined
  const unit = m[2]
  const multiplier =
    unit === undefined || unit === 'em'
      ? value
      : unit === '%'
        ? value / 100
        : headingSizePx
          ? value / headingSizePx
          : undefined
  if (multiplier === undefined) return undefined
  return multiplier >= 0.7 && multiplier <= 3 ? round(multiplier, 3) : undefined
}

/** `text-transform` → the record's two-value case. `capitalize` and `lowercase` have no field, so they are
 *  refused instead of being flattened into `none`. */
function cssTextCase(raw: string | undefined): 'none' | 'upper' | undefined {
  const s = raw?.trim().toLowerCase()
  if (s === 'uppercase') return 'upper'
  if (s === 'none') return 'none'
  return undefined
}

/** The first family in a font stack, unquoted: `'Inter', sans-serif` → `Inter`. `var(...)` indirection is
 *  refused — resolving it needs the whole stylesheet, which this module never sees. */
function firstFontFamily(raw: string | undefined): string | undefined {
  if (!raw || raw.toLowerCase().startsWith('var(')) return undefined
  return trimmed(raw.split(',')[0].replace(/^['"]|['"]$/g, ''))
}

// --- font families ---------------------------------------------------------------------------------
//
// MEASURED (2026-08-13): elevenlabs.io imported `type.display: 'Goudy Mediaeval Fallback'` and
// `type.body: 'Antoine Drop Caps Fallback'` off a site whose six `@font-face` files are Waldenburg, Inter
// and Geist Mono. Neither declared family exists, so the deck rendered in a system font AND lost the brand
// — a double failure that looks, in a screenshot, exactly like "the extractor found no fonts".
//
// A site declares these on purpose. Next.js / Vercel's font pipeline emits a metric-adjusted local face so
// text does not reflow when the real file lands, and the naming conventions in the wild are
// `<Name> Fallback`, `<Name>-fallback`, `__<Name>_Fallback` (with a build hash suffix) and
// `adjusted-<Name>`. All four describe the SAME thing: a stand-in shaped like the brand face, never the
// brand face. Emitting one as `type.display` is worse than emitting nothing, because nothing is a gap a
// preset fills.

/** A family a source named, after the fallback guard has had its say. */
interface ResolvedFamily {
  family: string
  /** True when the site's own declaration was a synthetic fallback and this name is a REPAIR — either the
   *  real family recovered from the `@font-face` list, or the marker stripped off the declared name.
   *  Callers stamp a repair `inferred`: the site did not state this, we worked it out. */
  repaired: boolean
}

/**
 * The family a source meant, cross-checked against the faces it actually ships.
 *
 * Order of evidence:
 *
 *   1. A declaration with no fallback marker is taken at its word. The `@font-face` cross-check applies only
 *      to a DISQUALIFIED candidate — a brand that sets `Helvetica Neue` ships no file for it and is still
 *      right, so "prefer a family with a file" must never overrule a legitimate declaration.
 *   2. A fallback whose stripped base name matches a shipped face: that is not a guess, it is two CSS
 *      declarations agreeing (`font-family: Goudy Mediaeval Fallback` over `@font-face { Goudy Mediaeval }`),
 *      so it stays measured and takes the FACE's own casing, which is the name that loads.
 *   3. A fallback that matches nothing, on a page that ships faces: the dominant shipped family. A guess,
 *      but an evidence-backed one — it is the family the page spent the most files on — and strictly better
 *      than a name no browser can resolve. This is what turns elevenlabs.io into Waldenburg.
 *   4. A fallback on a page with no faces at all: the stripped base name. `__Inter_Fallback` is named AFTER
 *      the real family, so the base is usually right and may well be a system or CDN face we never saw.
 *   5. Nothing declared: nothing returned. Picking the dominant shipped family out of silence would fill a
 *      gap that `styleGaps` should be reporting, and a preset filling it honestly is the better outcome.
 */
function resolveFamily(declared: string | undefined, faces: readonly StyleFontFace[]): ResolvedFamily | undefined {
  // Quotes come off first: a fallback family is quoted in CSS far more often than a plain one is, so leaving
  // them on would hide the marker from the test below.
  const name = trimmed(firstDeclaredFamily(declared))
  if (!name) return undefined
  if (!isFallbackFamily(name)) return reconcileWithShipped(name, faces)

  const base = stripFallbackMarker(name)
  if (base) {
    const shipped = faces
      .map((face) => trimmed(face.family))
      .filter((family): family is string => family !== undefined && !isFallbackFamily(family))
    const match = shipped.find((family) => family.toLowerCase() === base.toLowerCase())
    if (match) return { family: match, repaired: false }
  }

  const dominant = dominantShippedFamily(faces)
  if (dominant) return { family: dominant, repaired: true }
  return base ? { family: base, repaired: true } : undefined
}

/**
 * A declared family the browser can ACTUALLY resolve.
 *
 * THE BUG THIS EXISTS FOR (granola.ai, proven off a saved session 2026-08-13): the page declares
 * `font-family: 'Quadrant Notepad'` while the only face it ships is `@font-face { font-family: 'Quadrant' }`.
 * We faithfully carried `Quadrant Notepad` into the generated CSS, no such face existed, and every heading
 * fell back to sans-serif. It was invisible because `Melange` — the BODY font — matched exactly, so the
 * slides looked nearly right while the display face had never once rendered, for any brand.
 *
 * A name is only useful if a file answers to it. So when the declared family matches no shipped face but a
 * shipped face is clearly the same typeface — one name is a prefix of the other, which is what marketing
 * names like "Quadrant Notepad" over a file called "Quadrant" look like — take the SHIPPED name.
 *
 * Deliberately conservative: an exact match wins outright, and a page that ships nothing keeps its declared
 * name, because a family with no local file may still resolve through Google Fonts (`ensureBrandFont`).
 * Only a provable mismatch is repaired.
 */
function reconcileWithShipped(name: string, faces: readonly StyleFontFace[]): ResolvedFamily {
  const shipped = faces
    .map((face) => trimmed(face.family))
    .filter((family): family is string => family !== undefined && !isFallbackFamily(family))
  if (shipped.length === 0) return { family: name, repaired: false }

  const lower = name.toLowerCase()
  if (shipped.some((family) => family.toLowerCase() === lower)) return { family: name, repaired: false }

  // Longest first: with both "Quadrant" and "Quadrant Notepad Display" shipped, the more specific name is
  // the better answer for a declaration that named neither exactly.
  const related = shipped
    .filter((family) => {
      const f = family.toLowerCase()
      return f.startsWith(lower) || lower.startsWith(f)
    })
    .sort((a, b) => b.length - a.length)
  if (related[0]) return { family: related[0], repaired: true }

  return { family: name, repaired: false }
}

/** The generics a stack ends in, and the OS-font keywords a stack starts with (`-apple-system,
 *  BlinkMacSystemFont, Inter` means Inter). Never a brand's face, so never the family a kit meant. */
const GENERIC_FAMILIES = new Set([
  'sans-serif',
  'serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  '-apple-system',
  'blinkmacsystemfont',
  '-moz-system-font',
  '-ms-system-font',
  'ui-sans-serif',
  'ui-serif',
  'ui-monospace',
  'ui-rounded',
  'inherit',
  'initial',
])

/**
 * The FIRST real family of what a source declared, which may be a whole CSS stack.
 *
 * The Deck core kit (2026-09-05) held `headingFont: "Inter, sans-serif"`: the PDF read had stored the
 * stack where a name belongs. Taken whole, `fontStack` quoted it as one family, the recipe's tokens read
 * `--a-font-display: 'Inter, sans-serif', sans-serif`, and a writer that copied the token verbatim set
 * the whole document in the system face while the panel said Inter. The generic at the tail is what
 * `fontStack` appends anyway, so nothing is lost by dropping it here.
 */
function firstDeclaredFamily(declared: string | undefined): string | undefined {
  if (!declared) return undefined
  for (const part of declared.split(',')) {
    const name = part.trim().replace(/^['"]|['"]$/g, '').trim()
    if (name && !GENERIC_FAMILIES.has(name.toLowerCase())) return name
  }
  return undefined
}

/** Every convention that means "this is a metric-adjustment face". Matched on word boundaries that include
 *  `-` and `_`, so `__Inter_Fallback_e66fe9` and `Inter-fallback` are caught alongside the spaced form. A
 *  real family called "Fallback" would be a false positive; no such face exists, and the alternative is
 *  shipping a family name that cannot load. */
function isFallbackFamily(name: string): boolean {
  return /(^|[\s\-_])fallback([\s\-_]|$)/i.test(name) || /^adjusted[\s\-_]/i.test(name)
}

/** The brand name inside a fallback name, or undefined when stripping leaves nothing to keep. The build
 *  hash Next.js appends (`__Inter_Fallback_e66fe9`) goes with the marker — it identifies the build, not
 *  the typeface. */
function stripFallbackMarker(name: string): string | undefined {
  const s = name.trim()
  // next/font's own shape, where the real family is the part BETWEEN the markers and its spaces became
  // underscores on the way in: `__Playfair_Display_Fallback_e66fe9` → `Playfair Display`.
  const nextFont = /^__(.+?)_fallback(?:_[0-9a-z]+)?$/i.exec(s)
  if (nextFont) return trimmed(nextFont[1].replace(/_/g, ' '))
  const stripped = s.replace(/^adjusted[\s\-_]+/i, '').replace(/[\s\-_]+fallback(?:[\s\-_]+[0-9a-z]+)?$/i, '')
  const kept = trimmed(stripped)
  // Nothing was removed (a bare "Fallback"), so there is no brand name in here to keep.
  return kept && kept.toLowerCase() !== s.toLowerCase() ? kept : undefined
}

/** The family a page spends the most `@font-face` files on. Ties go to the earliest declared, because the
 *  extractor walks the stylesheet in order and a site leads with the face it cares about. */
function dominantShippedFamily(faces: readonly StyleFontFace[]): string | undefined {
  const counts = new Map<string, { family: string; files: number; at: number }>()
  faces.forEach((face, at) => {
    const family = trimmed(face.family)
    if (!family || isFallbackFamily(family)) return
    const key = family.toLowerCase()
    const seen = counts.get(key)
    if (seen) seen.files += 1
    else counts.set(key, { family, files: 1, at })
  })
  const ranked = [...counts.values()].sort((a, b) => b.files - a.files || a.at - b.at)
  return ranked[0]?.family
}

/**
 * The weights one family's `@font-face` files actually ship.
 *
 * Takes the RESOLVED family rather than `bs.headingFont`, so the list always describes the family that
 * landed in `type.display`. Reading it off the declared name instead is how elevenlabs.io got no weights at
 * all: nothing ships a file for `Goudy Mediaeval Fallback`.
 *
 * Deliberately duplicated from `moodboardStyle.headingFaceWeights`, which is module-private: importing it
 * would pull the live prompt serializer into this leaf, and this layer has to be able to change its own
 * rules without moving generation output. The mapping is the same — `bold` is 700, an absent or `normal`
 * weight is 400 — because both read the same wire shape.
 */
function shippedWeights(faces: readonly StyleFontFace[], family: string | undefined): string[] | undefined {
  const target = family?.trim().toLowerCase()
  if (!target || faces.length === 0) return undefined
  const weights = unique(
    faces
      .filter((face) => face.family.trim().toLowerCase() === target)
      .map((face) => (face.weight === 'bold' ? '700' : !face.weight || face.weight === 'normal' ? '400' : face.weight)),
  )
  return weights.length > 0 ? weights.sort((a, b) => Number(a) - Number(b)) : undefined
}

// --- small utilities -------------------------------------------------------------------------------

function trimmed(raw: string | undefined | null): string | undefined {
  const s = raw?.trim()
  return s ? s : undefined
}

function compact<T>(values: (T | undefined)[]): T[] {
  return values.filter((v): v is T => v !== undefined)
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)]
}

function round(value: number, places: number): number {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

function labelled(label: string, sentence: string | undefined): string | undefined {
  const text = trimmed(sentence)
  return text ? `${label}: ${text}` : undefined
}

function joinLines(lines: (string | undefined)[]): string | undefined {
  const kept = compact(lines)
  return kept.length > 0 ? kept.join('\n') : undefined
}

/** A stable, readable id fragment. Ids are derived rather than generated so the same source always
 *  produces the same record — a playground diffing two runs should see style differences, not new ids. */
function slug(raw: string): string {
  return (
    raw
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'unnamed'
  )
}

/** A website's host as a display name: `https://www.x.ai/about` → `x.ai`. */
function hostLabel(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return undefined
  }
}
